import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Tooltip, Box } from '@mui/material'

import { TableActions } from '../table-actions'
import { BlacklistCheckbox, WhitelistCheckbox } from '../checkbox-actions'
import { TableUrlPagination } from '../../../../ui'
import { EMAIL_MANAGEMENT_TABLE_COLUMNS } from '../../constants/email-management-table.constants'
import { emailManagementDataPreparation } from '../../logics'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const EmailManagementTable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const emailManagement = useSelector((state) => state.emailManagement)
  const { data, search, pagination = {}, isLoading } = emailManagement || {}
  const { total_emails: totalEntries } = pagination
  const combos = useSelector((state) => state.combos)

  const formattedColumns = useMemo(
    () =>
      EMAIL_MANAGEMENT_TABLE_COLUMNS.map((column) => {
        const baseColumn = {
          accessorKey: column.field,
          header:
            column.title ||
            formatMessage({ id: `pages.emailManagement.table.columns.${column.field}.title` }),
          size: column.width,
          muiTableHeadCellProps: { align: column.align || 'left' },
        }

        if (column.field === 'actions') {
          return {
            ...baseColumn,
            Cell: ({ row }) => <TableActions rowData={row.original} />,
            muiTableBodyCellProps: { align: 'center' },
          }
        }

        if (column.field === 'blacklist_actions' || column.field === 'whitelist_actions') {
          return {
            ...baseColumn,
            Cell: ({ row }) =>
              column.field === 'blacklist_actions' ? (
                <BlacklistCheckbox rowData={row.original} />
              ) : (
                <WhitelistCheckbox rowData={row.original} />
              ),
            muiTableBodyCellProps: { align: 'center' },
          }
        }

        const renderCellWithEllipsis = ({ cell }) => {
          let value = cell.getValue() ?? ''

          if (column.comboId && combos[column.comboId]?.data) {
            const lookup = getLookup(combos[column.comboId].data)
            value = lookup[value] ?? value
          }

          return (
            <Tooltip title={value} placement="bottom-start">
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {value}
              </Box>
            </Tooltip>
          )
        }

        return {
          ...baseColumn,
          Cell: renderCellWithEllipsis,
          muiTableBodyCellProps: { align: column.align || 'left' },
        }
      }),
    [combos, formatMessage]
  )

  const processedData = useMemo(() => {
    if (!data?.length) return []
    return emailManagementDataPreparation({ data, intl })
  }, [data, intl])

  const tableOptions = useMemo(
    () => ({
      muiTableProps: {
        sx: {
          tableLayout: 'fixed',
        },
      },
      toolbar: false,
      selection: false,
      draggable: false,
    }),
    []
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={isLoading}
      onRowClick={null}
    />
  )
}
